import { useMutation, useQuery } from '@tanstack/react-query';
import { errorHandler } from 'lib/functions/errors';
import { showAlert } from 'lib/functions/utils';
import { NewDocument } from 'pages/Finance/Components/Financing/Confirming/components/Simulator/components/DocumentSelector/components/AddDocumentsConfirming/';
import { Dispatch } from 'redux';

import { requestHandler } from '../../api';
import {
  GetConfirmingRequestOverviewResponse,
  GetConfirmingRequestsResponse,
  GetDocumentsAvailableForConfirmingResponse,
} from './types';

export const useConfirmingRequests = (dispatch: Dispatch, query: string) =>
  useQuery({
    queryKey: ['confirming', 'requests', query],
    queryFn: async () => {
      const response = await getConfirmingRequests(dispatch, query);
      if (response) return response as GetConfirmingRequestsResponse;
      return {
        data: [],
        pagination: {
          page: 1,
          pageSize: 10,
          total: 0,
          totalPages: 0,
        },
      };
    },
    staleTime: 1000 * 60 * 2,
    retry: 1,
  });

export const getDocumentsAvailableForConfirming = async (
  dispatch: Dispatch,
  data: any,
  query: string,
): Promise<GetDocumentsAvailableForConfirmingResponse | undefined> => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/confirming/documents/available?${query}`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const createConfirmingOperation = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/confirming/request`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const editConfirmingOperation = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/confirming/request`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getConfirmingRequests = async (
  dispatch: Dispatch,
  query: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/confirming?${query}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getConfirmingRequestById = async (
  dispatch: Dispatch,
  id: number,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/confirming/${id}`,
    dispatch,
  });
  if (response) return response.data;
};

export const useConfirmingRequestOverview = (
  dispatch: Dispatch,
  requestId: string,
) =>
  useQuery({
    queryKey: ['confirming', 'request', requestId, 'overview'],
    queryFn: async () => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/confirming/${requestId}/overview`,
        dispatch,
      });
      if (response)
        return response.data as GetConfirmingRequestOverviewResponse;
      return undefined;
    },
    enabled: !!requestId,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

export const getClientsBankAccounts = async (
  dispatch: Dispatch,
  clients: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/confirming/clients/bankAccounts`,
    data: clients,
    dispatch,
  });
  if (response) return response.data;
};

export const savePayments = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/confirming/payments/${data.id}`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const updateStatusConfirmingRequest = async (
  dispatch: Dispatch,
  id: number,
  data: any,
) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/confirming/request/updateStatus/${id}`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export type AssignOwnerToConfirmingRequestOptions = {
  onSuccess?: (requestId: number) => void;
  onError?: () => void;
};

export const assignOwnerToConfirmingRequest = (
  dispatch: Dispatch,
  options?: AssignOwnerToConfirmingRequestOptions,
) =>
  useMutation({
    mutationKey: ['confirming', 'assignOwner'],
    mutationFn: async ({
      requestId,
      ownerId,
    }: {
      requestId: number;
      ownerId: number;
    }) => {
      const response = await requestHandler({
        method: 'patch',
        url: `internal/confirming/request/${requestId}/assign-owner`,
        dispatch,
        data: { ownerId },
      });
      if (response) return response.data;
      return undefined;
    },
    onSuccess: (_data, variables) => {
      showAlert(dispatch, 'success', 'Ejecutivo actualizado correctamente');
      options?.onSuccess?.(variables.requestId);
    },
    onError: (error) => {
      errorHandler(dispatch, error);
      options?.onError?.();
    },
  });

export const createDocumentsConfirming = async (
  dispatch: Dispatch,
  data: {
    documents: NewDocument[];
    stakeholderIdentifier: string;
  },
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/confirming/documents/create`,
    data,
    dispatch,
  });
  if (response) return response.data;
};
